/**
 * GENERADOR DE EXCEL
 *
 * Un `.xlsx` es un ZIP con unos cuantos XML dentro, asi que se escribe a mano:
 * meter una biblioteca de hojas de calculo entera —y su superficie de
 * seguridad— por generar un parte de horas seria desproporcionado.
 *
 * El ZIP se escribe SIN comprimir (metodo «store»). Un fichero guardado tal cual
 * es un ZIP perfectamente valido, se ahorra implementar deflate, y para unas
 * hojas de texto la diferencia de tamaño no le importa a nadie.
 *
 * Lo que sale de aqui NO es un volcado: lleva titulo, la linea de filtros con la
 * que se genero, cabecera en el navy de la casa, columnas a su ancho, fechas que
 * Excel reconoce como fechas y una fila de total. Un parte de horas se reenvia a
 * una gestoria y a un cliente, y tiene que poder mirarse sin arreglarlo antes.
 *
 * Todo ocurre en el navegador. No se pide nada a ningun servidor.
 */

// --- CRC32, que es lo unico que el formato ZIP exige calcular -----------------

const TABLA_CRC = (() => {
  const tabla = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    tabla[i] = c >>> 0;
  }
  return tabla;
})();

function crc32(datos: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < datos.length; i += 1) {
    c = TABLA_CRC[(c ^ datos[i]!) & 0xff]! ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

// --- Escritor de ZIP ----------------------------------------------------------

interface Entrada {
  nombre: string;
  datos: Uint8Array;
}

function escribirZip(entradas: Entrada[]): Blob {
  const codificador = new TextEncoder();
  const locales: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let desplazamiento = 0;

  const u16 = (v: number) => [v & 0xff, (v >>> 8) & 0xff];
  const u32 = (v: number) => [v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff];

  for (const entrada of entradas) {
    const nombre = codificador.encode(entrada.nombre);
    const suma = crc32(entrada.datos);
    const tamano = entrada.datos.length;

    const cabecera = new Uint8Array([
      ...u32(0x04034b50),
      ...u16(20), // version necesaria
      ...u16(0x0800), // nombres en UTF-8
      ...u16(0), // metodo: sin comprimir
      ...u16(0), // hora
      ...u16(0x21), // fecha (1 de enero de 1980, la mas antigua que admite)
      ...u32(suma),
      ...u32(tamano),
      ...u32(tamano),
      ...u16(nombre.length),
      ...u16(0),
    ]);

    const bloque = new Uint8Array(cabecera.length + nombre.length + tamano);
    bloque.set(cabecera, 0);
    bloque.set(nombre, cabecera.length);
    bloque.set(entrada.datos, cabecera.length + nombre.length);
    locales.push(bloque);

    const ficha = new Uint8Array([
      ...u32(0x02014b50),
      ...u16(20), // version que lo creo
      ...u16(20), // version necesaria
      ...u16(0x0800),
      ...u16(0),
      ...u16(0),
      ...u16(0x21),
      ...u32(suma),
      ...u32(tamano),
      ...u32(tamano),
      ...u16(nombre.length),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(0),
      ...u32(desplazamiento),
    ]);
    const fichaCompleta = new Uint8Array(ficha.length + nombre.length);
    fichaCompleta.set(ficha, 0);
    fichaCompleta.set(nombre, ficha.length);
    central.push(fichaCompleta);

    desplazamiento += bloque.length;
  }

  const tamanoCentral = central.reduce((suma, b) => suma + b.length, 0);
  const cierre = new Uint8Array([
    ...u32(0x06054b50),
    ...u16(0),
    ...u16(0),
    ...u16(entradas.length),
    ...u16(entradas.length),
    ...u32(tamanoCentral),
    ...u32(desplazamiento),
    ...u16(0),
  ]);

  // Se copia todo a un unico buffer: asi el tipo que ve `Blob` es inequivoco.
  const piezas = [...locales, ...central, cierre];
  const total = piezas.reduce((suma, p) => suma + p.length, 0);
  const salida = new Uint8Array(total);
  let cursor = 0;
  for (const pieza of piezas) {
    salida.set(pieza, cursor);
    cursor += pieza.length;
  }

  return new Blob([salida.buffer as ArrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

// --- Hojas --------------------------------------------------------------------

export type Celda = string | number;

/** Cómo se escribe cada columna. Se declara por columna, no por celda. */
export type Formato = 'texto' | 'fecha' | 'horas' | 'euros' | 'entero';

export interface Hoja {
  nombre: string;
  /** Título en grande. Si falta, la hoja empieza directamente por la tabla. */
  titulo?: string;
  /** Línea de contexto: con qué filtros se generó. Es lo que hace auditable el fichero. */
  subtitulo?: string;
  /**
   * Cabeceras de columna. Si no se dan, se toma la PRIMERA fila como cabecera,
   * que es como se llamaba a esto antes y sigue funcionando igual.
   */
  cabeceras?: string[];
  /** Ancho de cada columna, en caracteres. */
  anchos?: number[];
  /** Formato de cada columna. Por defecto, texto o número según el valor. */
  formatos?: Formato[];
  filas: Celda[][];
  /** Última fila, destacada. Va separada de `filas` para que no se pueda ordenar con ellas. */
  total?: Celda[];
}

const escapar = (texto: string): string =>
  texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Convierte 0 en A, 25 en Z, 26 en AA. */
function columna(indice: number): string {
  let n = indice;
  let letras = '';
  do {
    letras = String.fromCharCode(65 + (n % 26)) + letras;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return letras;
}

/**
 * Fecha ISO a número de serie de Excel: dias desde el 30 de diciembre de 1899.
 *
 * Se convierte a proposito, en vez de dejar el texto: una columna de fechas de
 * verdad se ordena y se filtra en Excel, y una de texto no. Se calcula en UTC
 * porque una fecha sin hora no tiene huso, y usar el del navegador haria que el
 * mismo dia saliera distinto segun quien descargue.
 */
function serieFecha(iso: string): number | null {
  const encaja = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!encaja) return null;
  const ms = Date.UTC(Number(encaja[1]), Number(encaja[2]) - 1, Number(encaja[3]));
  return Math.round(ms / 86_400_000) + 25_569;
}

// Indices de `cellXfs`, en el mismo orden en que se declaran mas abajo.
const ESTILO = {
  normal: 0,
  cabecera: 1,
  titulo: 2,
  subtitulo: 3,
  fecha: 4,
  horas: 5,
  euros: 6,
  entero: 7,
  totalTexto: 8,
  totalHoras: 9,
  totalEuros: 10,
  totalEntero: 11,
} as const;

const ESTILO_POR_FORMATO: Record<Formato, number> = {
  texto: ESTILO.normal,
  fecha: ESTILO.fecha,
  horas: ESTILO.horas,
  euros: ESTILO.euros,
  entero: ESTILO.entero,
};

const ESTILO_TOTAL_POR_FORMATO: Record<Formato, number> = {
  texto: ESTILO.totalTexto,
  fecha: ESTILO.totalTexto,
  horas: ESTILO.totalHoras,
  euros: ESTILO.totalEuros,
  entero: ESTILO.totalEntero,
};

function celdaXml(valor: Celda, ref: string, estilo: number, formato: Formato): string {
  const s = estilo === 0 ? '' : ` s="${estilo}"`;

  if (formato === 'fecha' && typeof valor === 'string') {
    const serie = serieFecha(valor);
    if (serie !== null) return `<c r="${ref}"${s}><v>${serie}</v></c>`;
  }
  if (typeof valor === 'number' && Number.isFinite(valor)) {
    return `<c r="${ref}"${s}><v>${valor}</v></c>`;
  }
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${escapar(
    String(valor),
  )}</t></is></c>`;
}

function hojaXml(hoja: Hoja): string {
  // Sin `cabeceras` explicitas, la primera fila hace de cabecera: es como se
  // llamaba a esto antes de que las hojas tuvieran titulo.
  const cabeceras = hoja.cabeceras ?? (hoja.filas[0] as string[] | undefined) ?? [];
  const cuerpo = hoja.cabeceras ? hoja.filas : hoja.filas.slice(1);
  const columnas = Math.max(cabeceras.length, ...hoja.filas.map((f) => f.length), 1);
  const formato = (c: number): Formato => hoja.formatos?.[c] ?? 'texto';

  const lineas: string[] = [];
  let fila = 0;
  const siguiente = () => (fila += 1);

  if (hoja.titulo) {
    lineas.push(
      `<row r="${siguiente()}" ht="21" customHeight="1">${celdaXml(hoja.titulo, `A${fila}`, ESTILO.titulo, 'texto')}</row>`,
    );
  }
  if (hoja.subtitulo) {
    lineas.push(
      `<row r="${siguiente()}">${celdaXml(hoja.subtitulo, `A${fila}`, ESTILO.subtitulo, 'texto')}</row>`,
    );
  }
  if (hoja.titulo || hoja.subtitulo) siguiente(); // una fila en blanco antes de la tabla

  const filaCabecera = siguiente();
  lineas.push(
    `<row r="${filaCabecera}">${cabeceras
      .map((c, i) => celdaXml(c, `${columna(i)}${filaCabecera}`, ESTILO.cabecera, 'texto'))
      .join('')}</row>`,
  );

  for (const datos of cuerpo) {
    const r = siguiente();
    lineas.push(
      `<row r="${r}">${datos
        .map((v, i) =>
          celdaXml(v, `${columna(i)}${r}`, ESTILO_POR_FORMATO[formato(i)], formato(i)),
        )
        .join('')}</row>`,
    );
  }

  const ultimaDatos = fila;

  if (hoja.total) {
    const r = siguiente();
    lineas.push(
      `<row r="${r}">${hoja.total
        .map((v, i) =>
          celdaXml(v, `${columna(i)}${r}`, ESTILO_TOTAL_POR_FORMATO[formato(i)], formato(i)),
        )
        .join('')}</row>`,
    );
  }

  const cols = hoja.anchos
    ? `<cols>${hoja.anchos
        .map((a, i) => `<col min="${i + 1}" max="${i + 1}" width="${a}" customWidth="1"/>`)
        .join('')}</cols>`
    : '';

  // La cabecera se queda fija al desplazarse: un parte de horas de doscientas
  // lineas sin eso obliga a subir y bajar para saber que columna se esta viendo.
  const panel = `<sheetViews><sheetView workbookViewId="0" showGridLines="0"><pane ySplit="${filaCabecera}" topLeftCell="A${filaCabecera + 1}" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>`;

  // Y se deja el filtro puesto, para que quien reciba el fichero pueda seguir
  // acotando sin tener que volver a la aplicacion.
  const filtro =
    ultimaDatos > filaCabecera
      ? `<autoFilter ref="A${filaCabecera}:${columna(columnas - 1)}${ultimaDatos}"/>`
      : '';

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">${panel}${cols}<sheetData>${lineas.join('')}</sheetData>${filtro}</worksheet>`;
}

/*
 * Los estilos, en el navy y el oro de la casa. El orden de `cellXfs` es el que
 * fija los indices de `ESTILO`: si se anade uno, va al final.
 */
const ESTILOS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="3">
<numFmt numFmtId="164" formatCode="dd/mm/yyyy"/>
<numFmt numFmtId="165" formatCode="0.0"/>
<numFmt numFmtId="166" formatCode="#,##0.00\\ &quot;€&quot;"/>
</numFmts>
<fonts count="5">
<font><sz val="11"/><name val="Calibri"/><color rgb="FF1F2C51"/></font>
<font><b/><sz val="11"/><name val="Calibri"/><color rgb="FFF6F4EF"/></font>
<font><b/><sz val="16"/><name val="Calibri"/><color rgb="FF1F2C51"/></font>
<font><sz val="10"/><name val="Calibri"/><color rgb="FF8A8578"/></font>
<font><b/><sz val="11"/><name val="Calibri"/><color rgb="FF1F2C51"/></font>
</fonts>
<fills count="4">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FF1F2C51"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFF4EEE1"/><bgColor indexed="64"/></patternFill></fill>
</fills>
<borders count="2">
<border><left/><right/><top/><bottom/><diagonal/></border>
<border><left/><right/><top style="thin"><color rgb="FFC5A55E"/></top><bottom/><diagonal/></border>
</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="12">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment vertical="center"/></xf>
<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment vertical="center"/></xf>
<xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="166" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="1" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="0" fontId="4" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="165" fontId="4" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="166" fontId="4" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="1" fontId="4" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"/>
</cellXfs>
</styleSheet>`;

/** Construye el libro entero y lo devuelve como Blob listo para descargar. */
export function construirLibro(hojas: Hoja[]): Blob {
  const codificador = new TextEncoder();
  const texto = (s: string) => codificador.encode(s);

  const tipos = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
${hojas
  .map(
    (_, i) =>
      `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
  )
  .join('\n')}
</Types>`;

  const relacionesRaiz = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

  const libro = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>${hojas
    .map((h, i) => `<sheet name="${escapar(h.nombre)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
    .join('')}</sheets>
</workbook>`;

  const relacionesLibro = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${hojas
  .map(
    (_, i) =>
      `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
  )
  .join('\n')}
<Relationship Id="rId${hojas.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

  return escribirZip([
    { nombre: '[Content_Types].xml', datos: texto(tipos) },
    { nombre: '_rels/.rels', datos: texto(relacionesRaiz) },
    { nombre: 'xl/workbook.xml', datos: texto(libro) },
    { nombre: 'xl/_rels/workbook.xml.rels', datos: texto(relacionesLibro) },
    { nombre: 'xl/styles.xml', datos: texto(ESTILOS) },
    ...hojas.map((hoja, i) => ({
      nombre: `xl/worksheets/sheet${i + 1}.xml`,
      datos: texto(hojaXml(hoja)),
    })),
  ]);
}

/** Lanza la descarga en el navegador. Nada sale de la maquina. */
export function descargar(blob: Blob, nombre: string): void {
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  // Se libera en el siguiente ciclo, cuando el navegador ya ha leido el blob.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
